import type { AsyncData } from '#app'
import type { NitroFetchOptions } from 'nitropack'
import { joinURL, withQuery } from 'ufo'

interface FetchOptions extends NitroFetchOptions<string> {
    transform?: (data: any) => any
    useAsyncData?: boolean
}

export const useStatamic = () => {
    const { $preview } = useNuxtApp() as {
        $preview?: Ref<{ token: string } | null>
    }
    const apiUrl = joinURL(useRuntimeConfig().public.statamicUrl, '/api')

    return async <T>(url: string, fetchOptions: FetchOptions = {}): Promise<AsyncData<T, any>> => {
        try {
            let endpoint = joinURL(apiUrl, url)

            if ($preview?.value) {
                // Add the preview token to the query string
                endpoint = withQuery(endpoint, {
                    token: $preview.value.token,
                })
            }

            fetchOptions.useAsyncData = fetchOptions.useAsyncData ?? true
            fetchOptions.method = fetchOptions.method || 'GET'

            const headers: Record<string, any> = {
                ...fetchOptions.headers,
                Accept: 'application/json',
            }

            // workaround for a Cloudflare bug
            const credentialsSupported = 'credentials' in Request.prototype

            const cacheKey = `${fetchOptions.method} ${endpoint}`

            return useAsyncData(
                cacheKey,
                async () =>
                    await $fetch(endpoint, {
                        ...fetchOptions,
                        credentials: credentialsSupported ? 'include' : undefined,
                        headers,
                    }),
                {
                    getCachedData: (key) => {
                        const cachedData = useNuxtData<T>(key)

                        if (fetchOptions.method !== 'GET' || !cachedData.data.value) {
                            // not a GET, or no cached data - don't use the cache
                            if (import.meta.dev) {
                                console.log(`[Statamic: ${fetchOptions.method}]`, endpoint)
                            }

                            return null
                        }

                        // check if there's cached data we can return
                        if (import.meta.dev) {
                            console.log(`[Statamic: CACHE HIT]`, endpoint)
                        }

                        return cachedData.data.value
                    },
                    transform: (data: any) => {
                        if (fetchOptions.transform) {
                            data = fetchOptions.transform(data)
                        }

                        // get rid of the "data" wrapper statamic adds
                        return data.data ? data.data : data
                    },
                },
            )
        } catch (error: any) {
            console.error('[Statamic: Error]', error)

            throw createError({
                statusCode: error.response?.status,
                statusMessage: error.response?.statusText,
                data: error.response?._data,
            })
        }
    }
}
